import {Component, inject} from '@angular/core';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {UserService} from "../../../core/user/services/user.service";
import {CommunicationRestService} from "../../../core/communication/api/communication.rest-service";
import {CommunicationDialogComponent} from "./communication-dialog/communication-dialog.component";
import {MatDialog} from "@angular/material/dialog";

type ConfigKey = 'TRACK_TRACE' | 'BULK_BILL' | 'PAYBACK' | 'ONLINE_OFFERS';

type CommunicationType = {
  value: ConfigKey;
  label: string;
  tooltip: string;
};

@Component({
  selector: 'app-communication',
  templateUrl: './communication.component.html',
  styleUrl: './communication.component.scss',
  standalone: false
})
export class CommunicationComponent {

  public pharmacies: any[] = [];
  public selectedIdf: string = "";
  private configs: any[] = [];
  public pharmaTypes: CommunicationType[] = [];
  public success = false;
  public error = false;
  public message = '';
  private communicationRestService= inject(CommunicationRestService);

  public userService = inject(UserService);
  private dialog = inject(MatDialog);

  public types: CommunicationType[] = [
    {
      value: 'TRACK_TRACE',
      label: 'Belieferungsinformationen',
      tooltip: 'Wir versorgen Sie mit relevanten Informationen betreffend Ihrer Belieferung. (Feiertage, Techn. Störungen, Verspätungen etc.)'
    },
    {
      value: 'BULK_BILL',
      label: 'Sammelrechnung',
      tooltip: 'Wir benachrichtigen Sie über die Verfügbarkeit Ihrer Sammelrechnung im Apothekenportal'
    },
    /*{
      value: 'PAYBACK',
      label: 'PAYBACK Sammelrechnung',
      tooltip: 'Wir benachrichtigen Sie über die Verfügbarkeit Ihrer PAYBACK Sammelrechnung im Apothekenportal'
    },
    {
      value: 'ONLINE_OFFERS',
      label: 'Online Sparangebote',
      tooltip: 'Wir benachrichtigen Sie, sobald ein neues Online Sparangebot für Sie zur Verfügung steht.'
    },*/
  ];

  constructor() {
    this.userService.loadAllIdfs().subscribe(phar => {
      this.pharmacies = phar;
      if (this.pharmacies?.length) {
        this.selectPharmacy(this.pharmacies[0].idf);
      }
    });
  }

  getUserTypes(): void {
    const typ = [...this.types];
    const selectedPharmacy = this.pharmacies.find((pharmacy: any) => pharmacy.idf === this.selectedIdf);
    const bulkBill: boolean = selectedPharmacy?.bulk_bill;


    if (!bulkBill) {
      const i = typ.findIndex(type => type.value === 'BULK_BILL');
      if (i > -1) {
        typ.splice(i, 1);
      }
    }

    this.pharmaTypes = typ;
  }

  changeConfig($event: MatSlideToggleChange, type: CommunicationType, idf: string): void {
    if (type.value === 'TRACK_TRACE') {
      return;
    }

    if ($event.checked) {
      this.openDialog(type, idf, $event.source);
    } else {
      this.communicationRestService.disable(idf, type.value).subscribe(result => {
        const config = this.configs.find(c => c.type === type.value);
        if (!result.error_code) {
          if (config) {
            config.active = false;
          }
        } else {
          if (config) {
            config.active = true;
          }
          $event.source.checked = true;
          this.success = false;
          this.error = true;
          this.message = 'Das Abonnement konnte nicht deaktiviert werden. Bitte versuchen Sie es später erneut.';
        }
      });
    }
  }

  openDialog(type: CommunicationType, idf: string, toggleRef?: any): void {
    const defaultEmail = this.getDialogDefaultEmail(type.value, idf);
    const existingConfig = this.configs?.find(config => config.type === type.value);
    const previousActive = existingConfig ? !!existingConfig.active : false;
    const selectedConfig = this.getSelectedConfig(type, defaultEmail);
    const dialogRef = this.dialog.open(CommunicationDialogComponent, {
      width: '700px',
      data: {type, idf, config: selectedConfig, defaultEmail}
    });

    dialogRef.afterClosed().subscribe(result => {
      this.message = '';

      if (!result) {
        this.success = false;
        this.error = false;
        return;
      }

      if (result.success) {
        const i = this.configs.findIndex(config => config.type === result.config.type);
        if (i < 0) {
          this.configs.push(result.config);
        } else {
          this.configs[i] = result.config;
        }

        this.success = true;
        this.error = false;
        const matchedType = this.types.find(t => t.value === result.config.type);
        this.message = matchedType
          ? `Ihre Kontaktdaten für das Abonnement ${matchedType.label} wurden erfolgreich gespeichert.`
          : 'Konfiguration erfolgreich gespeichert.';
        return;
      }

      this.success = false;
      if (!result.errorCode) {
        this.error = false;
        if (existingConfig) {
          existingConfig.active = previousActive;
        }
        if (toggleRef) {
          toggleRef.checked = previousActive;
        }
      } else {
        this.error = true;
        this.message = 'Beim Speichern Ihrer Konfiguration ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
        if (toggleRef) {
          toggleRef.checked = previousActive;
        }
      }
    });
  }

  getSelectedConfig(type: CommunicationType, defaultEmail: string | null): any {
    const selected = this.configs?.find(config => config.type === type.value);
    const fallbackEmail = this.getFallbackEmail(type.value, defaultEmail);

    if (selected) {
      const selectedEmails = this.getConfigEmails(selected);
      const phoneNumbers = selected.phoneNumbers ?? selected.phone_numbers ?? [];
      if (type.value === 'TRACK_TRACE' && selectedEmails.length === 0 && fallbackEmail) {
        return {
          ...selected,
          emails: [fallbackEmail],
          phoneNumbers
        };
      }

      return {
        ...selected,
        emails: selectedEmails,
        phoneNumbers
      };
    }

    if (type.value === 'BULK_BILL') {
      return {
        active: true,
        phoneNumbers: [],
        emails: [],
        type: 'BULK_BILL'
      };
    }

    if (type.value === 'TRACK_TRACE' && fallbackEmail) {
      return {active: true, phoneNumbers: [], emails: [fallbackEmail], type: 'TRACK_TRACE'};
    }

    return null;
  }

  private getDefaultEmailForIdf(idf: string): string | null {
    const pharmacy = this.pharmacies.find((pharma: any) => pharma.idf === idf);
    const mainIdf = this.pharmacies.find((pharma: any) => pharma.idf === this.userService.mainIdf());
    const candidates = [
      pharmacy?.email,
      pharmacy?.email_address,
      pharmacy?.mail,
      mainIdf?.email,
      this.userService.getUser()?.email
    ];

    for (const candidate of candidates) {
      if (typeof candidate !== 'string') {
        continue;
      }

      const normalized = candidate.trim();
      if (normalized.length > 0) {
        return normalized;
      }
    }

    return null;
  }

  private getDialogDefaultEmail(type: ConfigKey, idf: string): string | null {
    const accountEmail = this.getDefaultEmailForIdf(idf);
    return this.getFallbackEmail(type, accountEmail);
  }

  private getFallbackEmail(type: ConfigKey, accountEmail: string | null): string | null {
    if (type === 'TRACK_TRACE') {
      return accountEmail;
    }

    return null;
  }

  private getConfigEmails(config: any): string[] {
    const emails = config?.emails ?? config?.email_addresses ?? config?.emailAddresses ?? [];
    return (emails as string[])
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0);
  }

  isConfigActive(type: CommunicationType): boolean {
    const selectedConfig = this.configs?.find(config => config.type === type.value);
    return selectedConfig ? selectedConfig.active : false;
  }

  selectPharmacy(idf: string) {
    this.selectedIdf = idf;
    this.getUserTypes();
    this.communicationRestService.getConfigs(idf).subscribe(configs => {
      this.configs = configs.return_object ?? [];
    });
  }
}
